import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Loader2, Check } from "lucide-react";

import { toast } from "sonner";

const FOLLOW_UP = {
  detractor: "Was koennten wir besser machen?",
  passive: "Was wuerde Ihre Bewertung verbessern?",
  promoter: "Was gefaellt Ihnen besonders?",
};

export function NpsSurvey({ onClose }) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const category = score === null ? null : score <= 6 ? "detractor" : score <= 8 ? "passive" : "promoter";

  const submit = async () => {
    if (score === null) return;
    setSending(true);
    try {
      await api.post("/nps", { score, comment: comment.trim() || null });
      setSent(true);
    } catch { toast.error("Bewertung konnte nicht gesendet werden"); }
    setSending(false);
  };

  const dismiss = async () => {
    try { await api.post("/nps/dismiss"); } catch {}
    onClose();
  };

  if (sent) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" data-testid="nps-overlay">
        <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-6 text-center" data-testid="nps-success">
          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-success" />
          </div>
          <p className="font-heading text-base font-bold mb-1">Vielen Dank!</p>
          <p className="font-body text-sm text-muted-foreground">Ihre Bewertung hilft uns, Spurwechsel weiterzuentwickeln.</p>
          <Button variant="ghost" size="sm" className="font-body text-sm mt-4" onClick={onClose} data-testid="nps-close-success">Schliessen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" onClick={dismiss} data-testid="nps-overlay">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="nps-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <p className="font-heading text-base font-bold">Kurze Frage</p>
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" data-testid="nps-dismiss-x">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          <p className="font-body text-sm leading-relaxed">
            Wie wahrscheinlich ist es, dass Sie Spurwechsel einem Freund oder Kollegen empfehlen wuerden?
          </p>

          {/* Score buttons */}
          <div>
            <div className="flex gap-1 justify-between" data-testid="nps-scale">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setScore(i === score ? null : i)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                    score === i
                      ? i <= 6 ? "bg-error text-white" : i <= 8 ? "bg-warning text-white" : "bg-success text-white"
                      : "bg-muted hover:bg-muted/80 text-foreground"
                  }`}
                  data-testid={`nps-btn-${i}`}
                >
                  {i}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="font-body text-[10px] text-muted-foreground">Unwahrscheinlich</span>
              <span className="font-body text-[10px] text-muted-foreground">Sehr wahrscheinlich</span>
            </div>
          </div>

          {/* Follow-up */}
          {score !== null && category && (
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value.slice(0, 1000))}
              placeholder={FOLLOW_UP[category]}
              className="font-body text-sm min-h-[60px] resize-none"
              data-testid="nps-comment"
            />
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" className="font-body text-sm" onClick={dismiss} data-testid="nps-later-btn">
              Spaeter
            </Button>
            <Button
              size="sm"
              disabled={score === null || sending}
              className="font-body text-sm bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
              onClick={submit}
              data-testid="nps-submit-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Absenden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
